import type { DiscountRequestItem } from 'api/mappers/promoMap'
import type { GuestCheckoutOrderRequest } from 'api/guestCheckoutOrder'
import { STRIPE_PAYMENT_ELEMENT_CARD_TYPE, type PaymentMethod } from 'constants/payment'
import { SOURCE_TYPES } from 'constants/source'
import type { PendingGhaVoucher } from 'lib/metasearch/pendingGhaVoucher'
import { serialiseOccupancy } from 'lib/search/occupancy'

/** Guest checkout only supports a Stripe pay-in-full card payment. */
export const GUEST_CHECKOUT_PAYMENT_METHOD: PaymentMethod = STRIPE_PAYMENT_ELEMENT_CARD_TYPE

interface BuildGuestCheckoutOrderRequestInput {
  contact: App.Checkout.TravellerFormData
  currencyCode: string
  regionCode: GuestCheckoutOrderRequest['cart']['region_code']
  items: Array<unknown>
  discountItems: Array<DiscountRequestItem>
  payments: App.PaymentPlan
  transactionKey: string
  recaptchaResponse: string
  pendingGhaVoucher?: PendingGhaVoucher
  /** Enquiry id for the property this voucher covers — order items carry no offer id. */
  ghaBedbankSessionId?: string
  includeGhaVoucher?: boolean
}

export function buildGuestCheckoutOrderRequest(
  input: BuildGuestCheckoutOrderRequestInput,
): GuestCheckoutOrderRequest {
  const { contact, currencyCode, regionCode, items, discountItems, payments, transactionKey, recaptchaResponse, pendingGhaVoucher, ghaBedbankSessionId, includeGhaVoucher = true } = input
  const payment = payments.payments[0]

  if (!contact.email || !contact.firstName || !contact.lastName) {
    throw new Error('Guest checkout contact details are incomplete')
  }

  if (payments.payments.length !== 1 || payment.type !== 'stripe' || typeof payment.currency !== 'string') {
    throw new Error('Guest checkout requires one Stripe pay-in-full payment')
  }

  if (!items.every((item): item is GuestCheckoutOrderRequest['cart']['items'][number] => (
    !!item &&
    typeof item === 'object' &&
    (item as Record<string, unknown>).type === 'bedbank' &&
    typeof (item as Record<string, unknown>).transaction_key === 'string' &&
    (item as Record<string, unknown>).transaction_key !== ''
  ))) {
    throw new Error('Guest checkout requires valid bedbank order items')
  }

  let ghaContext: Pick<GuestCheckoutOrderRequest, 'gha' | 'gpid'> = {}
  // A tokenless landing (GHA-585) has no gpid but still names its leg through the rate rule ID.
  // svc-order forwards that to svc-connection-google, which mints against the property's current
  // config. Neither identifier means this was never a metasearch landing, so no GHA context.
  if (pendingGhaVoucher?.token || pendingGhaVoucher?.rateRuleId) {
    const {
      token,
      accountType,
      propertyId,
      checkIn,
      checkOut,
      rooms,
      rateRuleId,
      source,
      advertisedPrice,
      advertisedCurrency,
    } = pendingGhaVoucher
    if (!propertyId || !checkIn || !checkOut || !rooms?.length) {
      throw new Error('Guest checkout GHA voucher context is incomplete')
    }

    const occupancy = serialiseOccupancy(rooms)
    const occupancyValue = occupancy[0]
    if (occupancy.length !== 1 || (occupancyValue !== '1' && occupancyValue !== '2')) {
      throw new Error('Guest checkout GHA voucher occupancy is unsupported')
    }

    ghaContext = {
      ...(token && { gpid: token }),
      gha: {
        property_id: propertyId,
        check_in: checkIn,
        check_out: checkOut,
        occupancy: [occupancyValue],
        is_cug_rate: !!source?.includes(SOURCE_TYPES.CUG_METASEARCH),
        has_voucher: includeGhaVoucher,
        ...(accountType && { account_type: accountType }),
        ...(rateRuleId && { rate_rule_id: rateRuleId }),
        ...(ghaBedbankSessionId && { bedbank_session_id: ghaBedbankSessionId }),
        // Both or neither: a price the service cannot compare against the charged currency buys
        // no hold, so there is no point sending it.
        ...(advertisedPrice != null && advertisedCurrency && {
          advertised_price: advertisedPrice,
          advertised_currency: advertisedCurrency,
        }),
      },
    }
  }

  return {
    contact: {
      email: contact.email,
      first_name: contact.firstName,
      last_name: contact.lastName,
      ...(contact.phone && { phone: contact.phone }),
      ...(contact.phonePrefix && { phone_prefix: contact.phonePrefix }),
    },
    cart: {
      currency_code: currencyCode,
      region_code: regionCode,
      items,
      discount_items: discountItems,
      payments: {
        currency: payments.currency,
        isDepositPayment: false,
        isDeferredPayment: false,
        isPaymentSchedulePayment: false,
        payments: [{
          amount: payment.amount,
          currency: payment.currency,
          type: 'stripe',
        }],
      },
    },
    ...ghaContext,
    transaction_key: transactionKey,
    recaptchaResponse,
  }
}
